import {
	AppointmentDetailModel,
	deleteAppointment,
	RecurringAppointmentOperationKind
} from "client/Api/Calendar/calendar";
import {
	ActionListItem,
	Toolbar,
	useConfirmationDialog
} from "client/Components";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import {
	generateTrackingId,
	isNil,
	isNotNil
} from "client/Misc/utils";
import {
	ReactElement,
	useCallback,
	useState
} from "react";
import {
	computeShouldShowCancelButton,
	computeShouldShowDeleteButton,
	getAppointmentDeleteMessage
} from "./CalendarContent/ViewAppointmentBox/ViewAppointmentBox";
import { localize } from "client_legacy/ndaw/culture";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { CancelAppointmentDialog } from "./CalendarContent/CancelAppointmentDialog";

type DeleteButtonProps = {
	onIsBusyChange: (isBusy: boolean) => void;
	onSuccess: () => void;
	appointmentDetailsView: AppointmentDetailModel;
	recurringOperationKind?: RecurringAppointmentOperationKind;
};

export const DeleteOrCancelButton = (props: DeleteButtonProps): ReactElement => {
	const {
		onIsBusyChange,
		onSuccess,
		appointmentDetailsView,
		recurringOperationKind
	} = props;

	const [cancelDialogState, setCancelDialogState] = useState<CancelDialogState>({ isOpen: false });

	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();

	const isSeries: boolean = isNotNil(appointmentDetailsView.masterId);

	const shouldShowDeleteButton: boolean = computeShouldShowDeleteButton(appointmentDetailsView);
	const shouldShowCancelButton: boolean = computeShouldShowCancelButton(appointmentDetailsView);

	const deleteHandler = useCallback(
		async (recurringOperationKind?: RecurringAppointmentOperationKind): Promise<void> => {
			onIsBusyChange(true);

			const result: TryResult<void> = await openConfirmationDialog({
				title: getAppointmentDeleteMessage(recurringOperationKind),
				confirmText: localize("_Delete"),
				trackingId: "delete-appointment-confirmation-dialog"
			});

			if (!result.wasSuccessful) {
				onIsBusyChange(false);
				return;
			}

			try {
				await deleteAppointment(
					{
						identifier: {
							id: appointmentDetailsView.id,
							calendarId: appointmentDetailsView.calendar.id
						},
						recurringOperationKind: isNil(recurringOperationKind)
							? { isSet: false }
							: { isSet: true, value: recurringOperationKind }
					},
					globalCancellation
				);

				onSuccess();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				onIsBusyChange(false);
				alertManager.showError("_GeneralErrorMessage");
			}

		},
		[
			alertManager,
			appointmentDetailsView.id,
			appointmentDetailsView.calendar,
			globalCancellation,
			onSuccess,
			onIsBusyChange,
			openConfirmationDialog
		]
	);

	const openCancelDialogHandler = useCallback(
		(recurringAppointmentOperationKind?: RecurringAppointmentOperationKind): void => {
			setCancelDialogState({
				isOpen: true,
				recurringAppointmentOperationKind
			});
		},
		[]
	);

	const closeCancelAppointmentDialogHandler = useCallback(
		(wasCancelled?: boolean) => {
			setCancelDialogState({ isOpen: false });

			if (wasCancelled) {
				onSuccess();
			}
		},
		[
			onSuccess
		]
	);

	const shouldShowDropDown: boolean = isSeries && isNil(recurringOperationKind);

	const renderDeleteButton = useCallback(
		(): ReactElement => {
			const singleButtonTextLocalizationKey: LocalizationKey = isNil(recurringOperationKind)
				|| recurringOperationKind === RecurringAppointmentOperationKind.Current
					? "_DeleteEvent"
					: "_DeleteEvents";

			return shouldShowDropDown ? (
				<Toolbar.MenuButton
					kind="secondary"
					text={localize("_Delete")}
					rightIcon="chevron-down"
					trackingId="delete-button"
				>
					<ActionListItem
						text={localize("_ThisEventOnly")}
						onClick={() => deleteHandler(RecurringAppointmentOperationKind.Current)}
						trackingId={generateTrackingId("delete-button", "|event-only")}
					/>
					<ActionListItem
						text={localize("_ThisAndAllFollowingEvents")}
						onClick={() => deleteHandler(RecurringAppointmentOperationKind.CurrentAndFollowing)}
						trackingId={generateTrackingId("delete-button", "|event-and-following")}
					/>
					<ActionListItem
						text={localize("_AllEventsInTheSeries")}
						onClick={() => deleteHandler(RecurringAppointmentOperationKind.All)}
						trackingId={generateTrackingId("delete-button", "|all-events")}
					/>
				</Toolbar.MenuButton>
			) : (
				<Toolbar.Button
					kind="secondary"
					text={localize(singleButtonTextLocalizationKey)}
					onClick={() => deleteHandler(recurringOperationKind)}
					trackingId="delete-button"
				/>
			);
		},
		[
			deleteHandler,
			recurringOperationKind,
			shouldShowDropDown
		]
	);

	const renderCancelButton = useCallback(
		(): ReactElement => {
			const singleButtonTextLocalizationKey: LocalizationKey = isNil(recurringOperationKind)
				|| recurringOperationKind === RecurringAppointmentOperationKind.Current
					? "_CancelEvent"
					: "_CancelEvents";

			return shouldShowDropDown ? (
				<Toolbar.MenuButton
					kind="secondary"
					text={localize("_Cancel")}
					rightIcon="chevron-down"
					trackingId="cancel-button"
				>
					<ActionListItem
						text={localize("_ThisEventOnly")}
						onClick={() => openCancelDialogHandler(RecurringAppointmentOperationKind.Current)}
						trackingId={generateTrackingId("cancel-button", "|event-only")}
					/>
					<ActionListItem
						text={localize("_ThisAndAllFollowingEvents")}
						onClick={() => openCancelDialogHandler(RecurringAppointmentOperationKind.CurrentAndFollowing)}
						trackingId={generateTrackingId("cancel-button", "|event-and-following")}
					/>
					<ActionListItem
						text={localize("_AllEventsInTheSeries")}
						onClick={() => openCancelDialogHandler(RecurringAppointmentOperationKind.All)}
						trackingId={generateTrackingId("cancel-button", "|all-events")}
					/>
				</Toolbar.MenuButton>
			) : (
				<Toolbar.Button
					kind="secondary"
					text={localize(singleButtonTextLocalizationKey)}
					onClick={() => openCancelDialogHandler(recurringOperationKind)}
					trackingId="cancel-button"
				/>
			);
		},
		[
			openCancelDialogHandler,
			recurringOperationKind,
			shouldShowDropDown
		]
	);

	return (
		<>
			{shouldShowDeleteButton && renderDeleteButton()}
			{shouldShowCancelButton && renderCancelButton()}
			{confirmationDialog}
			{cancelDialogState.isOpen && (
				<CancelAppointmentDialog
					appointmentIdentifier={{
						id: appointmentDetailsView.id,
						calendarId: appointmentDetailsView.calendar.id
					}}
					onClose={closeCancelAppointmentDialogHandler}
					recurringOperationKind={cancelDialogState.recurringAppointmentOperationKind}
				/>
			)}
		</>
	);
};

DeleteOrCancelButton.displayName = "DeleteOrCancelButton";

type CancelDialogState = {
	isOpen: false;
} | {
	isOpen: true;
	recurringAppointmentOperationKind?: RecurringAppointmentOperationKind;
};
